import type { ComputedRef } from 'vue'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

type EngagementTimer = {
	startTime: number
	endTime?: number
}

/**
 * ### useEngagementTimer
 * #### Composable for tracking engagement time across multiple items/sessions
 * Track how long users spend on modals, forms, or other interactive elements
 * - Includes automatic cleanup on route changes to prevent memory leaks
 */
export const useEngagementTimer = () => {
	const timers = ref<Map<string, EngagementTimer>>(new Map())

	/**
	 * Start tracking engagement for a specific key
	 * @param key - Unique identifier for the timer
	 */
	const startTimer = (key: string): void => {
		timers.value.set(key, {
			startTime: Date.now()
		})
	}

	/**
	 * Stop tracking engagement for a specific key and return the duration
	 * @param key - Unique identifier for the timer
	 * @returns Duration in milliseconds, or 0 if timer wasn't started
	 */
	const stopTimer = (key: string): number => {
		const timer = timers.value.get(key)
		if (!timer) {
			return 0
		}

		const endTime = Date.now()
		timer.endTime = endTime

		const duration = endTime - timer.startTime

		// Clean up the timer after stopping
		timers.value.delete(key)

		return duration
	}

	/**
	 * Get current engagement duration for a running timer
	 * @param key - Unique identifier for the timer
	 * @returns Current duration in milliseconds, or 0 if timer wasn't started (ComputedRef)
	 */
	const getCurrentDuration = (key: string): ComputedRef<number> => {
		const timer = timers.value.get(key)
		if (!timer) {
			return computed(() => 0)
		}

		return computed(() => Date.now() - timer.startTime)
	}

	/**
	 * Check if a timer is currently running
	 * @param key - Unique identifier for the timer
	 * @returns True if timer is running, false otherwise (ComputedRef)
	 */
	const isTimerRunning = (key: string): ComputedRef<boolean> => {
		const timer = timers.value.get(key)
		return computed(() => !!timer && !timer.endTime)
	}

	/**
	 * Stop and clean up a timer without returning duration
	 * @param key - Unique identifier for the timer
	 */
	const cancelTimer = (key: string): void => {
		timers.value.delete(key)
	}

	/**
	 * Clean up all timers
	 */
	const clearAllTimers = (): void => {
		timers.value.clear()
	}

	/**
	 * Get all active timer keys
	 * @returns Array of active timer keys (ComputedRef)
	 */
	const getActiveTimers = (): ComputedRef<string[]> => {
		const activeKeys: string[] = []
		for (const [ key, timer ] of timers.value) {
			if (timer && !timer.endTime) {
				activeKeys.push(key)
			}
		}
		return computed(() => activeKeys)
	}

	// Automatic cleanup on route changes
	if (import.meta.client) {
		const router = useRouter()
		if (router) {
			router.beforeEach((_to, from) => {
				if (from.name) {
					clearAllTimers()
				}
			})
		}
	}

	return {
		startTimer,
		stopTimer,
		getCurrentDuration,
		isTimerRunning,
		cancelTimer,
		clearAllTimers,
		getActiveTimers
	}
}
